import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  LinearProgress,
  useTheme,
  Autocomplete,
  Chip,
  Stack,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  Close as CloseIcon,
  PersonAdd as PersonAddIcon,
  Save as SaveIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import type {
  MedicalReservationRecord,
  MedicalReservationUpdateRequest,
} from '../../types/medicalReservation';

interface AssignModalProps {
  open: boolean;
  record: MedicalReservationRecord | null;
  onClose: () => void;
  onAssign: (id: string, assignedTo: string, notes?: string) => Promise<MedicalReservationRecord>;
}

/**
 * 診療予約レコード振り分けモーダル
 * 
 * 意図：
 * - 個別レコードの振り分け操作
 * - 振り分け先の選択と追加情報入力
 * - 振り分け処理の実行
 * 
 * 注意点：
 * - バリデーション処理
 * - 楽観的更新のエラーハンドリング
 * - 振り分け先の管理
 * 
 * 改善提案：
 * - 振り分け先の履歴管理
 * - 振り分けテンプレート機能
 * - 一括振り分け対応
 */
export const AssignModal: React.FC<AssignModalProps> = ({
  open,
  record,
  onClose,
  onAssign,
}) => {
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [assignedTo, setAssignedTo] = useState<string>('');
  const [notes, setNotes] = useState<string>('');

  // 振り分け先オプション（実際の運用では API から取得）
  const assignmentOptions = [
    { category: '看護師', options: ['看護師A', '看護師B', '看護師C'] },
    { category: '医師', options: ['田中医師', '佐藤医師', '山田医師'] },
    { category: '事務', options: ['事務部A', '事務部B', '総務課'] },
    { category: 'カウンセラー', options: ['カウンセラーX', 'カウンセラーY'] },
  ];

  // すべてのオプションをフラット化
  const allOptions = assignmentOptions.flatMap(group => group.options);

  // フォーム初期化
  useEffect(() => {
    if (record && open) {
      setAssignedTo(record.assignedTo || '');
      setNotes(record.notes || '');
      setError(null);
    }
  }, [record, open]);

  const handleClose = () => {
    setAssignedTo('');
    setNotes('');
    setError(null);
    onClose();
  };

  const handleAssign = async () => {
    if (!record || !assignedTo.trim()) {
      setError('振り分け先を入力してください');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await onAssign(record.id, assignedTo.trim(), notes.trim() || undefined);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : '振り分けに失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // バリデーション
  const isValid = () => {
    return assignedTo.trim().length > 0;
  };

  if (!record) return null;

  return (
    <Dialog
      open={open}
      onClose={!isLoading ? handleClose : undefined}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${theme.palette.divider}`,
          pb: 2,
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <AssignmentIcon sx={{ color: theme.palette.primary.main }} />
          <Box>
            <Typography variant="h6" fontWeight={600}>
              振り分け実行
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {record.patientName} ({record.anybotId})
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} size="small" disabled={isLoading}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {isLoading && <LinearProgress sx={{ mb: 2 }} />}
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Grid container spacing={3}>
            {/* 基本情報表示 */}
            <Grid item xs={12}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: theme.palette.grey[50],
                  borderRadius: 1,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  基本情報
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      担当医師
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {record.doctorInCharge}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      診療開始時間
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {record.consultationStartTime}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* 振り分け先選択 */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Autocomplete
                  freeSolo
                  options={allOptions}
                  groupBy={(option) => {
                    for (const group of assignmentOptions) {
                      if (group.options.includes(option)) {
                        return group.category;
                      }
                    }
                    return 'その他';
                  }}
                  value={assignedTo}
                  onChange={(event, newValue) => {
                    setAssignedTo(newValue || '');
                  }}
                  onInputChange={(event, newInputValue) => {
                    setAssignedTo(newInputValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="振り分け先"
                      placeholder="担当者や部署を選択または入力してください"
                      error={!isValid() && assignedTo.length > 0}
                      helperText={
                        !isValid() && assignedTo.length > 0
                          ? '振り分け先を入力してください'
                          : '既存の選択肢から選ぶか、新しい振り分け先を入力できます'
                      }
                      disabled={isLoading}
                    />
                  )}
                  renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => (
                      <Chip
                        label={option}
                        {...getTagProps({ index })}
                        key={index}
                        size="small"
                        color="primary"
                      />
                    ))
                  }
                  sx={{ mb: 1 }}
                />
              </FormControl>
              
              {/* カテゴリ別クイック選択ボタン */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  クイック選択:
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {assignmentOptions.map((group) => (
                    <Box key={group.category}>
                      {group.options.slice(0, 2).map((option) => (
                        <Button
                          key={option}
                          size="small"
                          variant="outlined"
                          onClick={() => setAssignedTo(option)}
                          disabled={isLoading}
                          sx={{
                            mr: 0.5,
                            mb: 0.5,
                            textTransform: 'none',
                            fontSize: '0.75rem',
                          }}
                        >
                          {option}
                        </Button>
                      ))}
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Grid>

            {/* 備考・メモ */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="振り分け理由・備考"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={isLoading}
                placeholder="振り分け理由や特記事項があれば記入してください..."
                helperText="振り分けの経緯や注意事項などを記録できます（任意）"
              />
            </Grid>
          </Grid>
        </motion.div>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Button 
          onClick={handleClose} 
          variant="outlined" 
          disabled={isLoading}
        >
          キャンセル
        </Button>
        <Button
          onClick={handleAssign}
          variant="contained"
          disabled={isLoading || !isValid()}
          startIcon={isLoading ? null : <PersonAddIcon />}
          sx={{
            bgcolor: theme.palette.success.main,
            '&:hover': {
              bgcolor: theme.palette.success.dark,
            },
          }}
        >
          {isLoading ? '振り分け中...' : '振り分け実行'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};